import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import _ from 'lodash';

import { Table, useParentSizeContext, DataTableProps, ColumnWithSorting, Loading } from '@tradingblock/components';
import {
  SizeType,
  CashieringBaseEntity,
  CashieringSearchRequest,
  FeedData,
  FeedCashieringUpdate,
} from '@tradingblock/types';
import { useBboxDimensions } from '../../../../blocks/shared/useBboxDimensions';
import { useDispatcher } from '../../../../data/global/hooks';
import { apiTokenSelector, useStateSelector } from '../../../../data/global/dataSelectors';
import { isFetchingCashieringSearchEntities } from '../../../../data/global/selectors/admin/cashieringSelectors';
import { useCashieringData } from '../useCashiering';
import {
  AdminCashieringActions,
  AdminCashieringSearchActions,
} from '../../../../data/global/actions/admin/AdminCashieringActions';
import { useFeedActions } from '../../../../data/feed/useFeedActions';
import { useFeatureFlag } from '@tradingblock/components';

const CashieringTableContent: React.FunctionComponent<DataTableProps<CashieringBaseEntity>> = props => {
  const b = useParentSizeContext();
  const [, ref] = useBboxDimensions<HTMLDivElement>(250);

  return (
    <div ref={ref} className="table-fixed-wrapper">
      <Table {...props} size={SizeType.lg} windowSize={b} />
    </div>
  );
};

export const CashieringTable: React.FunctionComponent<{
  handleSetSelectedRow: (index: number | undefined) => void;
  selectedRowIndex?: number;
  CashieringAdminReviewColumns: ColumnWithSorting<CashieringBaseEntity>[];
  tableId?: string;
  shouldScrollToSelected?: boolean;
  onScrollComplete?: () => void;
}> = ({
  handleSetSelectedRow,
  selectedRowIndex,
  CashieringAdminReviewColumns,
  tableId: propTableId,
  shouldScrollToSelected,
  onScrollComplete,
}) => {
  const { dispatch } = useDispatcher();
  const tableRef = useRef<HTMLDivElement>(null);
  const tableId = useMemo(
    () =>
      propTableId ||
      `cashiering-table-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
    [propTableId]
  );

  const { data, total, timeframe, ...filterData } = useCashieringData();
  const { sortBy, sortByOrder, pageSize, page } = filterData;

  const [isFetching, hasData] = useStateSelector(isFetchingCashieringSearchEntities);
  const { isEnabled: useLofiCashiering } = useFeatureFlag('lofi-cashiering');
  const oldFeedProviderStatus = useStateSelector(s => s.feedProvider.status);
  const cashieringFeedProviderStatus = useStateSelector(s => s.cashieringFeedProvider.status);
  const feedProviderStatus = useLofiCashiering ? cashieringFeedProviderStatus : oldFeedProviderStatus;
  const apiToken = useStateSelector(apiTokenSelector);
  const latestCashieringUpdateFeedDataFromWebsocket = useStateSelector(
    s => s.private.cashiering.latestCashieringUpdateDataFromWebsocket
  );
  const accountIdsSubscribedForCashieringUpdates = useStateSelector(
    s => s.private.cashiering.accountIdsSubscribedForCashieringUpdates
  );

  const cashieringSearch = sessionStorage.getItem(`cashieringSearch-${tableId}`);

  const { subscribeToCashieringUpdates, unsubscribeFromCashieringUpdates } = useFeedActions(apiToken ? apiToken : '');

  const prevLatestCashieringUpdateFeedDataFromWebsocket = useRef<FeedCashieringUpdate | null>(
    latestCashieringUpdateFeedDataFromWebsocket
  );
  const accountIdsSubscribedForCashieringUpdatesRef = useRef<number[]>(accountIdsSubscribedForCashieringUpdates);
  const componentSubscribedAccountIds = useRef<number[]>([]);
  const lastProcessedAccountIds = useRef<number[]>([]);
  const subscriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [shouldTriggerCashieringSearchRequest, setShouldTriggerCashieringSearchRequest] = useState<boolean>(false);
  const hasScrolledRef = useRef(false);
  const lastSelectedRowIndexRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    accountIdsSubscribedForCashieringUpdatesRef.current = accountIdsSubscribedForCashieringUpdates;
  }, [accountIdsSubscribedForCashieringUpdates]);

  // Handle row selection from sessionStorage (for highlighting)
  useEffect(() => {
    if (hasData && !isFetching) {
      if (!cashieringSearch) return;
      const cashieringSearchData = JSON.parse(cashieringSearch);
      const { index } = cashieringSearchData;
      handleSetSelectedRow(index);
      // Reset scroll flag when we restore from sessionStorage (indicates return from detail)
      hasScrolledRef.current = false;
      // Clear after processing
      sessionStorage.removeItem(`cashieringSearch-${tableId}`);
    }
  }, [hasData, isFetching, cashieringSearch, tableId, handleSetSelectedRow]);

  // Reset scroll flag when shouldScrollToSelected becomes true (indicates we're returning from detail)
  useEffect(() => {
    if (shouldScrollToSelected) {
      hasScrolledRef.current = false;
    }
  }, [shouldScrollToSelected]);

  // Scroll to selected row when returning from detail page
  useEffect(() => {
    // Check if we should scroll: flag is true, we have a selected row, data is loaded, and we haven't scrolled yet
    const shouldScroll =
      shouldScrollToSelected && selectedRowIndex !== undefined && hasData && !isFetching && !hasScrolledRef.current;

    if (shouldScroll) {
      hasScrolledRef.current = true;
      lastSelectedRowIndexRef.current = selectedRowIndex;

      // Function to attempt scrolling with retries
      const attemptScroll = (attempt = 1) => {
        if (tableRef.current) {
          const row = tableRef.current.querySelector(`.${tableId}-selected-row`);
          if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            onScrollComplete && onScrollComplete();
            return true;
          } else if (attempt < 5) {
            // Retry up to 5 times with increasing delays
            setTimeout(() => attemptScroll(attempt + 1), 200 * attempt);
          } else {
            // Final attempt failed, call onScrollComplete anyway
            onScrollComplete && onScrollComplete();
          }
        }
        return false;
      };

      // Start scrolling after a delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        attemptScroll();
      }, 800);

      return () => clearTimeout(timeoutId);
    }

    // Update ref when selectedRowIndex changes
    if (selectedRowIndex !== undefined) {
      lastSelectedRowIndexRef.current = selectedRowIndex;
    }
  }, [shouldScrollToSelected, selectedRowIndex, hasData, isFetching, tableId, onScrollComplete]);

  const rowClassName = useCallback(
    (row: any) => {
      if (row.index === selectedRowIndex) {
        return `${tableId}-selected-row`;
      }
      return '';
    },
    [selectedRowIndex, tableId]
  );

  useEffect(() => {
    dispatch(AdminCashieringActions.cashieringSearchRequest(filterData));
  }, [dispatch]);

  useEffect(() => {
    if (shouldTriggerCashieringSearchRequest) {
      dispatch(AdminCashieringActions.cashieringSearchRequest(filterData));
      setShouldTriggerCashieringSearchRequest(false);
    }
  }, [shouldTriggerCashieringSearchRequest]);

  const getCurrentSortBy = useCallback(
    (newDesc?: boolean) => {
      if (sortBy) {
        return { id: sortBy, desc: !_.isUndefined(newDesc) ? newDesc : sortByOrder === 'desc' };
      }
      return undefined;
    },
    [sortBy, sortByOrder]
  );

  const initialState = useMemo(() => {
    const currentSortBy = getCurrentSortBy();
    return {
      sortBy: currentSortBy ? [currentSortBy] : [],
      pageSize: _.isUndefined(pageSize) ? 5 : pageSize,
      pageIndex: page || 0,
      columnOrder: [],
    };
  }, [pageSize, page, getCurrentSortBy]);

  const pageCount = useMemo(() => {
    if (pageSize) {
      return _.ceil(total / pageSize);
    }
    return 0;
  }, [pageSize, total]);

  const onSort = useCallback(
    (sort: any[]) => {
      const sortVal = _.first(sort) || getCurrentSortBy(sortByOrder === 'asc');
      if (sortVal) {
        const { id, desc } = sortVal;
        const sortData: Pick<CashieringSearchRequest, 'sortBy' | 'sortByOrder'> = {
          sortBy: id,
          sortByOrder: desc === true ? 'desc' : 'asc',
        };
        dispatch(AdminCashieringSearchActions.setSortBy(sortData));
      } else {
        dispatch(AdminCashieringSearchActions.setSortBy({ sortBy: undefined, sortByOrder: undefined }));
      }
      handleSetSelectedRow(undefined);
    },
    [getCurrentSortBy]
  );

  const onPage = useCallback((page: number) => {
    dispatch(AdminCashieringSearchActions.setPage({ page }));
    handleSetSelectedRow(undefined);
  }, []);

  const onPageSize = useCallback((pageSize: number) => {
    dispatch(AdminCashieringSearchActions.setPageSize({ pageSize }));
    handleSetSelectedRow(undefined);
  }, []);

  //This is used to simplify data, since our columns only support specific lengths
  // We only will allow the transfer text to be 24 chars.
  const simplifiedData = useMemo(
    () =>
      data.map(cashieringItem => {
        if (cashieringItem.transfer && cashieringItem.transfer.text && cashieringItem.transfer.text.length >= 24) {
          cashieringItem.transfer.text = cashieringItem.transfer.text.slice(0, 21) + '...';
        }
        // attach the handleSetSelectedRow function to each row
        cashieringItem.handleSetSelectedRow = handleSetSelectedRow;
        return cashieringItem;
      }),
    [data]
  );

  // Memoize the account IDs to prevent unnecessary subscription updates
  const currentAccountIds = useMemo(() => {
    if (simplifiedData && simplifiedData.length > 0) {
      return [...new Set(simplifiedData.map(rowData => rowData.accountId))].sort();
    }
    return [];
  }, [simplifiedData]);

  useEffect(() => {
    if (feedProviderStatus === 'open' && apiToken && !isFetching) {
      if (currentAccountIds.length > 0) {
        // Clear any existing timeout
        if (subscriptionTimeoutRef.current) {
          clearTimeout(subscriptionTimeoutRef.current);
        }

        // Debounce the subscription logic to prevent duplicate requests
        subscriptionTimeoutRef.current = setTimeout(() => {
          // Check if we've already processed these exact account IDs
          const hasProcessedTheseIds = _.isEqual(currentAccountIds, lastProcessedAccountIds.current);
          if (hasProcessedTheseIds) {
            return;
          }

          // Only proceed if we actually have different account IDs than what we're currently subscribed to
          const currentSubscribedIds = accountIdsSubscribedForCashieringUpdates;
          const hasSignificantChange = !_.isEqual(currentAccountIds, currentSubscribedIds.sort());

          if (currentSubscribedIds.length === 0) {
            subscribeToCashieringUpdates(currentAccountIds);
            dispatch(AdminCashieringActions.setAccountIdsSubscribedForCashieringUpdates(currentAccountIds));
            componentSubscribedAccountIds.current = currentAccountIds;
            lastProcessedAccountIds.current = [...currentAccountIds];
          } else if (hasSignificantChange) {
            const accountIdsThatNeedToBeSubscribedForCashieringUpdates = currentAccountIds.filter(
              accountId => !currentSubscribedIds.includes(accountId)
            );
            const accountIdsThatStillNeedToBeSubscribedForCashieringUpdates = currentAccountIds.filter(accountId =>
              currentSubscribedIds.includes(accountId)
            );
            const accountIdsThatNoLongerNeedToBeSubscribedForCashieringUpdates = currentSubscribedIds.filter(
              accountId => !currentAccountIds.includes(accountId)
            );

            if (accountIdsThatNeedToBeSubscribedForCashieringUpdates.length > 0) {
              subscribeToCashieringUpdates(accountIdsThatNeedToBeSubscribedForCashieringUpdates);
              componentSubscribedAccountIds.current = [
                ...componentSubscribedAccountIds.current,
                ...accountIdsThatNeedToBeSubscribedForCashieringUpdates,
              ];
            }

            // Only unsubscribe if we have new data and the account IDs are truly no longer needed
            // This prevents unsubscribing during navigation when the component temporarily unmounts
            if (
              accountIdsThatNoLongerNeedToBeSubscribedForCashieringUpdates.length > 0 &&
              currentAccountIds.length > 0
            ) {
              unsubscribeFromCashieringUpdates(accountIdsThatNoLongerNeedToBeSubscribedForCashieringUpdates);
              componentSubscribedAccountIds.current = componentSubscribedAccountIds.current.filter(
                id => !accountIdsThatNoLongerNeedToBeSubscribedForCashieringUpdates.includes(id)
              );
            } else if (accountIdsThatNoLongerNeedToBeSubscribedForCashieringUpdates.length > 0) {
            }

            dispatch(
              AdminCashieringActions.setAccountIdsSubscribedForCashieringUpdates([
                ...accountIdsThatNeedToBeSubscribedForCashieringUpdates,
                ...accountIdsThatStillNeedToBeSubscribedForCashieringUpdates,
              ])
            );
            lastProcessedAccountIds.current = [...currentAccountIds];
          } else {
          }
        }, 100); // 100ms debounce delay
      } else {
        // Only unsubscribe if we truly have no data and no other components might need these subscriptions
        // This prevents unsubscribing during navigation
        if (accountIdsSubscribedForCashieringUpdates.length > 0 && !hasData) {
        }
      }
    }
  }, [feedProviderStatus, apiToken, currentAccountIds, accountIdsSubscribedForCashieringUpdates, isFetching, hasData]);

  // When there is a new cashiering update message sent via the websocket and the account ID and update type ID match a row's data then we are
  // refetching the table data.
  useEffect(() => {
    if (
      latestCashieringUpdateFeedDataFromWebsocket &&
      !_.isEqual(latestCashieringUpdateFeedDataFromWebsocket, prevLatestCashieringUpdateFeedDataFromWebsocket.current)
    ) {
      const matchingRow = simplifiedData.find(cashieringEvent => {
        // First check if account IDs match
        const accountIdMatches = cashieringEvent.accountId === latestCashieringUpdateFeedDataFromWebsocket.accountId;

        if (!accountIdMatches) {
          return false;
        }

        // Then check if the update type ID matches any of the entity IDs
        const updateTypeId = latestCashieringUpdateFeedDataFromWebsocket.updateTypeId;
        const updateType = latestCashieringUpdateFeedDataFromWebsocket.updateType;

        // Check transfer ID match
        if (cashieringEvent.transfer && cashieringEvent.transfer.transferId) {
          const transferId =
            typeof cashieringEvent.transfer.transferId === 'string'
              ? parseInt(cashieringEvent.transfer.transferId)
              : cashieringEvent.transfer.transferId;
          if (updateTypeId === transferId) {
            return true;
          }
        }

        // Check relationship ID match
        if (cashieringEvent.relationship && cashieringEvent.relationship.relationshipId) {
          const relationshipId =
            typeof cashieringEvent.relationship.relationshipId === 'string'
              ? parseInt(cashieringEvent.relationship.relationshipId)
              : cashieringEvent.relationship.relationshipId;
          if (updateTypeId === relationshipId) {
            return true;
          }
        }

        // Check transfer wire instruction ID match
        if (
          cashieringEvent.transferWireInstruction &&
          cashieringEvent.transferWireInstruction.transferWireInstructionId
        ) {
          const wireInstructionId =
            typeof cashieringEvent.transferWireInstruction.transferWireInstructionId === 'string'
              ? parseInt(cashieringEvent.transferWireInstruction.transferWireInstructionId)
              : cashieringEvent.transferWireInstruction.transferWireInstructionId;
          if (updateTypeId === wireInstructionId) {
            return true;
          }
        }

        return false;
      });

      if (matchingRow) {
        setShouldTriggerCashieringSearchRequest(true);
      } else {
        // Fallback: If we have data for this account but no exact match,
        // still refresh to catch any updates that might affect the table
        const hasAccountData = simplifiedData.some(
          row => row.accountId === latestCashieringUpdateFeedDataFromWebsocket.accountId
        );
        if (hasAccountData) {
          setShouldTriggerCashieringSearchRequest(true);
        }
      }
    }
  }, [latestCashieringUpdateFeedDataFromWebsocket, simplifiedData]);

  useEffect(() => {
    prevLatestCashieringUpdateFeedDataFromWebsocket.current = latestCashieringUpdateFeedDataFromWebsocket;
  }, [latestCashieringUpdateFeedDataFromWebsocket]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      // Clear any pending subscription timeout
      if (subscriptionTimeoutRef.current) {
        clearTimeout(subscriptionTimeoutRef.current);
        subscriptionTimeoutRef.current = null;
      }
      // No specific cleanup needed here as the subscription management is now handled by useFeedActions
    };
  }, []);

  return (
    <div ref={tableRef}>
      {isFetching && <Loading />}
      <useParentSizeContext.Provider>
        {!isFetching && (
          <>
            {hasData && (
              <CashieringTableContent
                columns={CashieringAdminReviewColumns}
                data={simplifiedData}
                initialState={initialState}
                sortable
                hasPagination
                manualSortBy
                manualPagination
                onSort={onSort}
                onPage={onPage}
                onPageSize={onPageSize}
                pageCount={pageCount}
                columnWidths={['10%', '10%', '5%', '30%', '10%', '10%', '15%', '1%', '7%', '1%', '1%']}
                rowClass={(row: any) => rowClassName(row)}
                tableKey="cashieringReviewTable"
              />
            )}
            {!hasData && (
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#fff',
                  background: 'var(--blue-dark-lighter)',
                  borderRadius: 'var(--radius)',
                  marginTop: '20px',
                }}
                role="status"
                aria-live="polite"
              >
                <i className="fal fa-search" style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.5 }}></i>
                <h3 style={{ color: '#fff', marginBottom: '8px' }}>No matching items found</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
                  No cashiering records match your current search criteria. Try adjusting your filters to see more
                  results.
                </p>
              </div>
            )}
          </>
        )}
      </useParentSizeContext.Provider>
    </div>
  );
};
